import React from 'react'
import { trpc } from '@/trpcReact'
import { Button } from '@/components/ui/button'
import { useLocalSearchParams, Link } from 'expo-router'
import { subscriptionStatus } from '@/constants/constants'
import Head from 'expo-router/head'
import { isNonEmptyArray } from '@/lib/utils.ts'

export default function AdminPage() {
  const params = useLocalSearchParams<{ subscriptionId?: string }>()
  const subscriptionId =
    typeof params.subscriptionId === 'string' ? params.subscriptionId : undefined

  const {
    data: sub,
    isLoading,
    error,
    refetch,
  } = trpc.getSubscription.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!subscriptionId },
  )
  const disableMutation = trpc.disableSubscriptionAccessForLicenseCheck.useMutation({
    onSuccess: () => refetch(),
    onError: (error) => {
      window.alert(`Error: ${error.message}`)
    },
  })
  const deleteMutation = trpc.deleteSubscription.useMutation({
    onSuccess: (data) => {
      const names = data.removedUsernames
      if (names.length > 0) {
        window.alert(`Removed GitHub users: ${names.join(', ')}`)
      }
      refetch()
    },
    onError: (error) => {
      window.alert(`Error: ${error.message}`)
    },
  })
  const enableMutation = trpc.enableSubscriptionAccess.useMutation({
    onSuccess: () => refetch(),
    onError: (error) => {
      window.alert(`Error: ${error.message}`)
    },
  })
  const serverCrashMutation = trpc.triggerServerCrash.useMutation()
  const anonymizeMutation = trpc.adminAnonymizeUser.useMutation({
    onSuccess: () => {
      window.alert('User anonymized successfully')
      refetch()
    },
    onError: (error) => {
      window.alert(`Error: ${error.message}`)
    },
  })
  const adminInviteMutation = trpc.adminCreateGithubInvite.useMutation({
    onSuccess: () => {
      window.alert('Invites sent successfully!')
      refetch()
    },
    onError: (error) => {
      window.alert(`Error: ${error.message}`)
    },
  })
  const refundMutation = trpc.refundSubscription.useMutation({
    retry: false,
    onSettled: () => {
      void refetch()
    },
  })

  // Header with login link, always visible
  const header = (
    <>
      <Head>
        <title>Admin - React Native Google Sign In</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <header className="container mx-auto px-4 py-8 flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Admin: Subscription</h1>
        <Link href="/auth/login?email=vonovak@gmail.com">
          <span className="text-primary hover:underline">Login</span>
        </Link>
      </header>
    </>
  )

  if (isLoading) {
    return (
      <>
        {header}
        <div className="p-8 text-center">Loading...</div>
      </>
    )
  }
  if (error) {
    return (
      <>
        {header}
        <div className="p-8 text-center text-destructive">Error: {error.message}</div>
      </>
    )
  }

  if (!subscriptionId) {
    return (
      <>
        {header}
        <div className="p-8 text-center">
          Provide a <code>subscriptionId</code>.
        </div>
      </>
    )
  }
  if (!sub) {
    return (
      <>
        {header}
        <div className="p-8 text-center">No subscriptions found.</div>
      </>
    )
  }

  const otherActionPending =
    adminInviteMutation.isPending ||
    disableMutation.isPending ||
    enableMutation.isPending ||
    deleteMutation.isPending ||
    anonymizeMutation.isPending

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-4 gap-4 flex flex-row">
        <a
          href="https://github.com/organizations/react-native-google-signin/settings/audit-log"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          View Audit Log
        </a>
        <a href="/configure-sub" className="text-primary hover:underline">
          Dashboard
        </a>
        <Button
          className="bg-warning hover:bg-warning/90 text-warning-foreground"
          size="sm"
          onClick={() => {
            const date = new Date()
            throw new Error(
              `${date.toISOString()}: This is a client-side test error to trigger Sentry crash reporting.`,
            )
          }}
        >
          Trigger client crash
        </Button>
        <Button
          className="bg-warning hover:bg-warning/90 text-warning-foreground"
          size="sm"
          onClick={() => {
            serverCrashMutation.mutate()
          }}
        >
          Trigger server crash
        </Button>
      </div>
      {refundMutation.error && (
        <p role="alert" className="mb-4 text-destructive">
          {refundMutation.error.message}
        </p>
      )}
      {refundMutation.data && (
        <p role="status" className="mb-4">
          Refund initiated: {refundMutation.data.formattedAmount}.{' '}
          {refundMutation.data.emailSent
            ? `Notification sent to ${refundMutation.data.email}.`
            : `The notification could not be sent. Email ${refundMutation.data.email} manually; the refund has already been initiated.`}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full border bg-card">
          <thead>
            <tr className="bg-secondary text-left">
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Started At</th>
              <th className="px-4 py-2">Last Renewal</th>
              <th className="px-4 py-2">Agreed At</th>
              <th className="px-4 py-2">Sub ID</th>
              <th className="px-4 py-2">User ID</th>
              <th className="px-4 py-2">Invited</th>
              <th className="px-4 py-2">Debugger uses</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t">
              <td className="px-4 py-2 capitalize">{sub.status}</td>
              <td className="px-4 py-2">{new Date(sub.started_at).toLocaleString()}</td>
              <td className="px-4 py-2">{new Date(sub.current_period_start).toLocaleString()}</td>
              <td className="px-4 py-2">
                {sub.license_agreement_date
                  ? new Date(sub.license_agreement_date).toLocaleString()
                  : 'N/A'}
              </td>
              <CopyCell value={sub.sub_id} />
              <CopyCell value={sub.user_id} />
              <td className="px-4 py-2">
                {sub.memberCount} / {sub.seats}
              </td>
              <td className="px-4 py-2">{sub.debuggerRequestsCount}</td>
              <td className="px-4 py-2">
                <Button
                  variant="destructive"
                  size="sm"
                  className="mr-2"
                  disabled={
                    sub.status === subscriptionStatus.revoked ||
                    refundMutation.isPending ||
                    refundMutation.isSuccess ||
                    otherActionPending
                  }
                  onClick={() => {
                    const renewalDate = new Date(sub.current_period_start).toLocaleString('en-US', {
                      dateStyle: 'medium',
                      timeStyle: 'medium',
                    })
                    if (
                      window.confirm(
                        `Have you manually reviewed and approved this refund for the period starting ${renewalDate}?\n\nGitHub activity is not checked automatically. Proceeding will revoke this subscription’s access, cancel future renewals, refund the full latest payment, and email the owner.`,
                      )
                    ) {
                      refundMutation.mutate({
                        subscriptionId: sub.sub_id,
                        eligibilityConfirmed: true,
                      })
                    }
                  }}
                >
                  {refundMutation.isPending ? 'Processing refund...' : 'Refund'}
                </Button>
                {/* Manage Invites Button */}
                <Button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  size="sm"
                  onClick={() => {
                    const input = window.prompt(
                      `Enter GitHub usernames (comma-separated)\nSubscription: ${sub.sub_id}\nSeats: ${sub.memberCount}/${sub.seats}`,
                    )
                    if (!input) return

                    const usernames = input
                      .split(',')
                      .map((u) => u.trim())
                      .filter((u) => u.length > 0)

                    if (!isNonEmptyArray(usernames)) {
                      window.alert('No valid usernames entered')
                      return
                    }

                    if (
                      window.confirm(
                        `Send invites to ${usernames.length} user(s): ${usernames.join(', ')}?`,
                      )
                    ) {
                      adminInviteMutation.mutate({
                        subscriptionId: sub.id,
                        githubUsernames: usernames,
                      })
                    }
                  }}
                  disabled={refundMutation.isPending || adminInviteMutation.isPending}
                >
                  {adminInviteMutation.isPending ? 'Sending...' : 'Invite Users'}
                </Button>
                {/* Disable Invites Button */}
                <Button
                  className="bg-warning hover:bg-warning/90 text-warning-foreground ml-2"
                  size="sm"
                  disabled={refundMutation.isPending || disableMutation.status === 'pending'}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Are you sure you want to disable invites for this subscription?',
                      )
                    ) {
                      const emailInput = window.prompt(
                        'Enter CC email addresses (optional, comma or space separated):',
                      )
                      let ccEmails: string[] | undefined

                      if (emailInput) {
                        const emailsArray = emailInput
                          .split(/[,\s]+/)
                          .map((email) => email.trim())
                          .filter((email) => email.length > 0)

                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                        const invalidEmails = emailsArray.filter((email) => !emailRegex.test(email))

                        if (invalidEmails.length > 0) {
                          window.alert(`Invalid email format: ${invalidEmails.join(', ')}`)
                          return
                        }

                        ccEmails = emailsArray
                      }

                      disableMutation.mutate({ subscriptionInternalId: sub.id, ccEmails })
                    }
                  }}
                >
                  {sub.status === subscriptionStatus.pendingLicenseCheck
                    ? 'Invites Already Disabled'
                    : disableMutation.status === 'pending'
                      ? 'Disabling...'
                      : 'Disable Invites & Send License Check Email'}
                </Button>
                {/* Enable Access Button */}
                {sub.status !== subscriptionStatus.active && (
                  <Button
                    className="bg-success hover:bg-success/90 text-success-foreground ml-2"
                    size="sm"
                    disabled={refundMutation.isPending || enableMutation.status === 'pending'}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Are you sure you want to enable access for this subscription?',
                        )
                      ) {
                        enableMutation.mutate({ subscriptionInternalId: sub.id })
                      }
                    }}
                  >
                    {enableMutation.status === 'pending'
                      ? 'Enabling...'
                      : 'Re-enable Subscription Access'}
                  </Button>
                )}
                {/* Delete Button */}
                <Button
                  variant="destructive"
                  size="sm"
                  className="ml-2"
                  disabled={refundMutation.isPending || deleteMutation.status === 'pending'}
                  onClick={() => {
                    if (
                      window.confirm('Are you sure you want to remove access of this subscription?')
                    ) {
                      deleteMutation.mutate({
                        subscriptionId: sub.sub_id,
                        status: subscriptionStatus.pendingLicenseCheck,
                      })
                    }
                  }}
                >
                  {deleteMutation.status === 'pending'
                    ? 'Deleting...'
                    : 'Remove GitHub Access & Set Pending License Check'}
                </Button>
                {/* Anonymize User (GDPR) */}
                {sub.status === subscriptionStatus.revoked && (
                  <Button
                    className="bg-warning hover:bg-warning/90 text-warning-foreground ml-2"
                    size="sm"
                    disabled={refundMutation.isPending || anonymizeMutation.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          'GDPR: Anonymize user? This will delete subscription members, reassign data to anonymous user, and delete the auth user (only if ALL their subscriptions are revoked).',
                        )
                      ) {
                        anonymizeMutation.mutate({ subscriptionId: sub.sub_id })
                      }
                    }}
                  >
                    {anonymizeMutation.isPending ? 'Anonymizing...' : 'GDPR Anonymize'}
                  </Button>
                )}
                {/* Revoke Button */}
                <Button
                  variant="destructive"
                  className="ml-2"
                  size="sm"
                  disabled={refundMutation.isPending || deleteMutation.status === 'pending'}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Are you sure you want to revoke this subscription (for cancelled license)?',
                      )
                    ) {
                      deleteMutation.mutate({
                        subscriptionId: sub.sub_id,
                        status: subscriptionStatus.revoked,
                      })
                    }
                  }}
                >
                  {deleteMutation.status === 'pending'
                    ? 'Revoking...'
                    : 'Remove GitHub Access & Set Revoked'}
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

function CopyCell({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false)
  return (
    <td
      className="px-4 py-2 font-mono text-xs cursor-pointer hover:bg-secondary"
      title="Click to copy"
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1000)
        })
      }}
    >
      {copied ? 'Copied!' : value}
    </td>
  )
}
